import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';

import { Cart, CartActions } from 'types/cart';

import {
  clearCartCookie,
  getCartFromCookie,
  setCartCookie,
} from 'utils/cartCookie';

type CartContextType = Cart & CartActions;

const CartContext = React.createContext<CartContextType | undefined>(undefined);

type CartProviderProps = {
  children: React.ReactNode;
};

export const CartProvider = ({ children }: CartProviderProps) => {
  const [cart, setCart] = useState<Cart>(() => getCartFromCookie());

  // Sync cart changes to cookie
  useEffect(() => {
    setCartCookie(cart);
  }, [cart]);

  const addToCart = useCallback(
    (catalogItemToken: string, quantity: number) => {
      if (quantity <= 0) return;

      setCart((prevCart) => {
        const existingItemIndex = prevCart.lineItems.findIndex(
          (item) => item.catalogItemToken === catalogItemToken,
        );

        if (existingItemIndex >= 0) {
          // Update existing item
          const updatedLineItems = [...prevCart.lineItems];

          updatedLineItems[existingItemIndex] = {
            ...updatedLineItems[existingItemIndex],
            quantity: updatedLineItems[existingItemIndex].quantity + quantity,
          };

          return {
            ...prevCart,
            lineItems: updatedLineItems,
          };
        } else {
          // Add new item
          return {
            ...prevCart,
            lineItems: [...prevCart.lineItems, { catalogItemToken, quantity }],
          };
        }
      });
    },
    [],
  );

  const updateCartItem = useCallback(
    (catalogItemToken: string, quantity: number) => {
      setCart((prevCart) => {
        if (quantity <= 0) {
          // Remove item if quantity is 0 or negative
          return {
            ...prevCart,
            lineItems: prevCart.lineItems.filter(
              (item) => item.catalogItemToken !== catalogItemToken,
            ),
          };
        }

        const existingItemIndex = prevCart.lineItems.findIndex(
          (item) => item.catalogItemToken === catalogItemToken,
        );

        if (existingItemIndex >= 0) {
          // Update existing item
          const updatedLineItems = [...prevCart.lineItems];

          updatedLineItems[existingItemIndex] = {
            ...updatedLineItems[existingItemIndex],
            quantity,
          };

          return {
            ...prevCart,
            lineItems: updatedLineItems,
          };
        } else {
          // Add new item
          return {
            ...prevCart,
            lineItems: [...prevCart.lineItems, { catalogItemToken, quantity }],
          };
        }
      });
    },
    [],
  );

  const removeFromCart = useCallback((catalogItemToken: string) => {
    setCart((prevCart) => ({
      ...prevCart,
      lineItems: prevCart.lineItems.filter(
        (item) => item.catalogItemToken !== catalogItemToken,
      ),
    }));
  }, []);

  const clearCart = useCallback(() => {
    const emptyCart: Cart = {
      orderToken: null,
      lineItems: [],
    };

    setCart(emptyCart);
    clearCartCookie();
  }, []);

  const getCartItemQuantity = useCallback(
    (catalogItemToken: string): number => {
      const item = cart.lineItems.find(
        (item) => item.catalogItemToken === catalogItemToken,
      );

      return item?.quantity || 0;
    },
    [cart.lineItems],
  );

  const getTotalItemCount = useCallback((): number => {
    return cart.lineItems.reduce((total, item) => total + item.quantity, 0);
  }, [cart.lineItems]);

  const getCartItems = useCallback(() => {
    return cart.lineItems;
  }, [cart.lineItems]);

  const isEmpty = useCallback((): boolean => {
    return cart.lineItems.length === 0;
  }, [cart.lineItems]);

  const contextValue: CartContextType = {
    ...cart,
    addToCart,
    updateCartItem,
    removeFromCart,
    clearCart,
    getCartItemQuantity,
    getTotalItemCount,
    getCartItems,
    isEmpty,
  };

  return (
    <CartContext.Provider value={contextValue}>{children}</CartContext.Provider>
  );
};

export const useCart = () => {
  const context = React.useContext(CartContext);

  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }

  return context;
};
